import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { 
  Calendar, Plus, ChevronLeft, ChevronRight, 
  Clock, User, X, Loader2, CheckCircle, XCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const AppointmentBadges = {
  types: {
    consultation: 'bg-brand-blue-50 border-brand-blue-200 text-brand-blue-700',
    follow_up: 'bg-brand-green-50 border-brand-green-200 text-brand-green-700',
    review: 'bg-brand-purple-50 border-brand-purple-200 text-brand-purple-700',
    checkup: 'bg-yellow-50 border-yellow-300 text-yellow-800',
    urgent: 'bg-brand-red-50 border-brand-red-200 text-brand-red-700'
  },
  typeDots: {
    consultation: 'bg-brand-blue',
    follow_up: 'bg-brand-green',
    review: 'bg-brand-purple',
    checkup: 'bg-yellow-500',
    urgent: 'bg-brand-red'
  },
  statuses: {
    confirmed: 'badge-success',
    scheduled: 'badge-info',
    completed: 'bg-neutral-light text-gray-700 border-neutral-medium',
    cancelled: 'badge-error'
  }
};

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  
  const [formData, setFormData] = useState({
    client_id: '',
    practitioner_id: '',
    type: 'consultation',
    appointment_date: '',
    appointment_time: '',
    duration_minutes: 60,
    notes: ''
  });

  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const fetchAppointments = useCallback(async () => {
    try {
      const response = await api.get('/appointments', {
        params: {
          month: month + 1,
          year: year
        }
      });
      setAppointments(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load appointments');
      setLoading(false);
    }
  }, [month, year]);

useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Fetch clients for dropdown
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await api.get('/clients');
        setClients(response.data);
      } catch (error) {
        toast.error('Failed to load clients');
      }
    };
    fetchClients();
  }, []);

  // Fetch practitioners for dropdown
  useEffect(() => {
    const fetchPractitioners = async () => {
      try {
        const response = await api.get('/auth/practitioners');
        setPractitioners(response.data);
      } catch (error) {
        toast.error('Failed to load practitioners');
      }
    };
    fetchPractitioners();
  }, []);

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(year, month + direction, 1));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const getDayAppointments = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return appointments.filter(apt => {
      const aptDate = apt.appointment_date?.split('T')[0];
      return aptDate === dateStr;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/appointments', formData);
      toast.success('Appointment created successfully');
      setShowNewAppointment(false);
setFormData({
        client_id: '',
        practitioner_id: '',
        type: 'consultation',
        appointment_date: '',
        appointment_time: '',
        duration_minutes: 60,
        notes: ''
      });
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to create appointment');
    }
    setSubmitting(false);
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.put(`/appointments/${id}`, { status });
      toast.success(`Appointment marked as ${status}`);
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const renderCalendar = () => {
    const weeks = [];
    let days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayAppointments = getDayAppointments(day);
      const isToday = day === today.getDate() && 
        month === today.getMonth() && 
        year === today.getFullYear();

      days.push(
        <div
          key={day}
          className={`p-2 border border-neutral-medium rounded-lg min-h-[80px] cursor-pointer hover:bg-neutral-light transition-colors ${
            isToday ? 'bg-brand-green-50/30 border-brand-green/30' : ''
          }`}
          onClick={() => {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            if (dayAppointments.length > 0) {
              setSelectedAppointment(dayAppointments[0]);
            }
          }}
        >
          <p className={`text-sm font-semibold mb-1 ${
            isToday ? 'text-brand-green' : 'text-neutral-dark'
          }`}>
            {day}
          </p>
          {dayAppointments.slice(0, 2).map((apt, idx) => (
            <div
              key={apt.id}
              className={`text-xs p-1 rounded mb-0.5 truncate ${
                AppointmentBadges.types[apt.type] || 'bg-gray-100'
              }`}
            >
              {formatTime(apt.appointment_time)}
            </div>
          ))}
          {dayAppointments.length > 2 && (
            <p className="text-xs text-neutral-gray mt-0.5">
              +{dayAppointments.length - 2} more
            </p>
          )}
        </div>
      );
    }

    while (days.length % 7 !== 0) {
      days.push(<div key={`end-empty-${days.length}`} className="p-2"></div>);
    }

    for (let i = 0; i < days.length; i += 7) {
      weeks.push(
        <div key={i} className="grid grid-cols-7 gap-1">
          {days.slice(i, i + 7)}
        </div>
      );
    }

    return weeks;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-brand-blue-100 rounded-lg">
            <Calendar size={24} className="text-brand-blue" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-neutral-dark">Appointments</h1>
            <p className="text-sm text-neutral-gray">Schedule and manage appointments</p>
          </div>
        </div>

        <button
          onClick={() => setShowNewAppointment(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={18} />
          <span>New Appointment</span>
        </button>
      </div>

      {/* Calendar Navigation */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-neutral-medium p-4">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-2 rounded-lg hover:bg-neutral-light"
        >
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-lg font-semibold text-neutral-dark">
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h2>
        <button
          onClick={() => navigateMonth(1)}
          className="p-2 rounded-lg hover:bg-neutral-light"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-medium text-neutral-gray py-2">
              {day}
            </div>
          ))}
        </div>
        {renderCalendar()}
      </div>

      {/* Upcoming Appointments List */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-medium p-4">
        <h3 className="text-lg font-semibold text-neutral-dark mb-4">Upcoming Appointments</h3>
        <div className="space-y-3">
          {appointments.filter(apt => apt.status !== 'completed' && apt.status !== 'cancelled').length === 0 ? (
            <p className="text-neutral-gray text-center py-4">No upcoming appointments</p>
          ) : (
            appointments
              .filter(apt => apt.status !== 'completed' && apt.status !== 'cancelled')
              .slice(0, 10)
              .map(apt => (
                <div
                  key={apt.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-neutral-medium hover:bg-neutral-light cursor-pointer transition-colors"
                  onClick={() => setSelectedAppointment(apt)}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full ${AppointmentBadges.typeDots[apt.type] || 'bg-gray-300'}`}></div>
                    <div>
                      <p className="text-sm font-medium text-neutral-dark">{`${apt.client_first_name} ${apt.client_last_name}`}</p>
                      <div className="flex items-center space-x-2 text-xs text-neutral-gray">
                        <span>{formatDate(apt.appointment_date)}</span>
                        <span>·</span>
                        <span>{formatTime(apt.appointment_time)}</span>
                        <span>·</span>
                        <span className="capitalize">{apt.type?.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    AppointmentBadges.statuses[apt.status] || 'bg-gray-100'
                  }`}>
                    {apt.status}
                  </span>
                </div>
              ))
          )}
        </div>
      </div>

      {/* New Appointment Modal */}
      {showNewAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-neutral-medium px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-xl font-semibold text-neutral-dark">New Appointment</h2>
              <button onClick={() => setShowNewAppointment(false)} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>

<form onSubmit={handleCreateAppointment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Client <span className="text-brand-red">*</span>
                </label>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
                  required
                >
                  <option value="">Select a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.first_name} {client.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Practitioner</label>
                <select
                  name="practitioner_id"
                  value={formData.practitioner_id}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
                >
                  <option value="">Select a practitioner...</option>
                  {practitioners.map(practitioner => (
                    <option key={practitioner.id} value={practitioner.id}>
                      {practitioner.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Appointment Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green bg-white"
                >
                  <option value="consultation">Consultation</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="review">Review</option>
                  <option value="assessment">Assessment</option>
                  <option value="initial_consultation">Initial Consultation</option>
                  <option value="program_review">Program Review</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Date <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="date"
                    name="appointment_date"
                    value={formData.appointment_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Time <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type="time"
                    name="appointment_time"
                    value={formData.appointment_time}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration (minutes)</label>
                <input
                  type="number"
                  name="duration_minutes"
                  value={formData.duration_minutes}
                  onChange={handleInputChange}
                  min={15}
                  max={240}
                  step={15}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-neutral-medium rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-medium">
                <button
                  type="button"
                  onClick={() => setShowNewAppointment(false)}
                  className="px-5 py-2.5 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-50"
                >
                  {submitting ? (
                    <span className="flex items-center space-x-2">
                      <Loader2 size={16} className="animate-spin" />
                      <span>Creating...</span>
                    </span>
                  ) : 'Create Appointment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-neutral-medium flex items-center justify-between">
              <h2 className="text-xl font-semibold text-neutral-dark">Appointment Details</h2>
              <button onClick={() => setSelectedAppointment(null)} className="p-1 hover:bg-neutral-light rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-brand-blue-100 rounded-lg">
                  <User size={20} className="text-brand-blue" />
                </div>
                <div>
                  <p className="font-medium text-neutral-dark">{`${selectedAppointment.client_first_name} ${selectedAppointment.client_last_name}`}</p>
                  <p className="text-sm text-neutral-gray">{selectedAppointment.client_phone || 'No phone'}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="p-2 bg-brand-green-100 rounded-lg">
                  <Calendar size={20} className="text-brand-green" />
                </div>
                <div>
                  <p className="font-medium text-neutral-dark">{formatDate(selectedAppointment.appointment_date)}</p>
                  <p className="text-sm text-neutral-gray">{formatTime(selectedAppointment.appointment_time)}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="p-2 bg-brand-purple-100 rounded-lg">
                  <Calendar size={20} className="text-brand-purple" />
                </div>
                <div>
                  <p className="font-medium text-neutral-dark capitalize">
                    {selectedAppointment.type?.replace(/_/g, ' ')}
                  </p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    AppointmentBadges.statuses[selectedAppointment.status] || 'bg-gray-100'
                  }`}>
                    {selectedAppointment.status}
                  </span>
                </div>
              </div>

              {selectedAppointment.practitioner_name && (
                <p className="text-sm text-neutral-gray">
                  <span className="font-medium">Practitioner:</span> {selectedAppointment.practitioner_name}
                </p>
              )}

              <p className="text-sm text-neutral-gray">
                <span className="font-medium">Duration:</span> {selectedAppointment.duration_minutes || 60} minutes
              </p>

              {selectedAppointment.notes && (
                <div>
                  <p className="text-sm font-medium text-neutral-dark mb-1">Notes</p>
                  <p className="text-sm text-neutral-gray">{selectedAppointment.notes}</p>
                </div>
              )}

              <div className="flex space-x-3 pt-4 border-t border-neutral-medium">
                {selectedAppointment.status !== 'completed' && (
                  <button
                    onClick={() => handleUpdateStatus(selectedAppointment.id, 'completed')}
                    className="flex-1 btn-primary flex items-center justify-center space-x-2"
                  >
                    <CheckCircle size={16} />
                    <span>Mark Complete</span>
                  </button>
                )}
                {selectedAppointment.status !== 'cancelled' && selectedAppointment.status !== 'completed' && (
                  <button
                    onClick={() => handleUpdateStatus(selectedAppointment.id, 'cancelled')}
                    className="flex-1 px-4 py-2 border border-brand-red-200 text-brand-red rounded-xl text-sm font-medium hover:bg-brand-red-50 flex items-center justify-center space-x-2"
                  >
                    <XCircle size={16} />
                    <span>Cancel</span>
                  </button>
                )}
                {selectedAppointment.status !== 'scheduled' && selectedAppointment.status !== 'confirmed' && (
                  <button
                    onClick={() => setSelectedAppointment(null)}
                    className="flex-1 px-4 py-2 border border-neutral-medium rounded-xl text-sm font-medium text-gray-700 hover:bg-neutral-light"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;
